import { getStyle } from '@coreui/coreui-pro/dist/js/coreui-utilities';
import { colorToRgba } from '../../../../util/UtilFunctions';
import moment from 'moment';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import { useDispatch, useSelector } from 'react-redux';
import roundTo from 'round-to';
import { setAlarm } from '../../../../redux/actions/general';
import { CsvExportFromApiButton, HorizontalSeparator, Spinner } from '../../../common';

import { CardTitle } from 'reactstrap';

import { PropTypes } from 'prop-types';

import ZoomHint from '../../../zoom/ZoomHint';
import { WIDGET_SETTINGS } from '../../../zoom/widgetSettings/HomeView';
import { PLAYOUT } from './../../../zoom/ZoomConstants';
import UsageModal from './../../../zoom/templates/UsageTemplate';

import { barChartZoomOptions } from '../../../../constants/chartjs';
import { HOME_VIEW } from '../../../../constants/general';

import {
   CenteredMsgShowing,
   ChartWrapper,
   ColoredText,
   HeightSetContainer,
   RightSide,
   SetChartHeight,
   SimpleFlexItem,
   SimpleFlexRow,
   TwoSideFlexContainer,
   TwoSideRow,
   WidgetBodyContainer,
   WidgetContainer,
   WidgetHeaderContainer,
} from '../../../../styledComponents/styledComponents';
import { addSeparatorAndThreeDecimalPlaces } from '../../../../util/NumberFormatter';

import { DefaultGrey } from '../../../../constants/colors';
import useDataFetch from '../../../../hooks/useDataFetch';
import {
   cancelTokenSelector,
   countriesSelector,
   endTimespanSelector,
   hashSelector,
   intervalTimespanSelector,
   startTimespanSelector,
   streamNamesSelector,
   tagsSelector,
   timespanSelector,
   titleTimespanSelector,
   usageSwitchSelector,
} from '../../../../redux/selectors/selectors';
import { generateStackedBarChartData } from '../../../../util/ChartJsProcessing';
import { getFilterQueryParams, getHttpHeader } from '../../../../util/FetchTools';
import TrafficHint from '../../../common/Hints/TrafficHint';
import ErrorDisplay from '../../misuse/ErrorDisplay';

const barChartOptions = barChartZoomOptions();

const stackedPropNames = ['moqUsage', 'h5liveUsage', 'rtmpUsage', 'hlsUsage', 'h5liveTokenUsage'];

const light = getStyle('--danger');
const red = '#FF3E41';
const dark = '#A53860';
const very_dark = '#5E0035';
const orange = '#da5816';

const USAGE_SETTINGS = {
   bytes: {
      unit: 'GB',
      chartLabelH5Live: 'H5Live [GB]',
      chartLabelRtmp: 'RTMP [GB]',
      chartLabelHls: 'HLS [GB]',
      chartLabelH5liveToken: 'H5Live Token [GB]',
      chartLabelMoq: 'MoQ [GB]',
      unitConversion: (value) => roundTo(value / 1e9, 3),
      resUnitPropNames: 'bytes',
   },
   playtime: {
      unit: 'h',
      chartLabelH5Live: 'H5Live [h]',
      chartLabelRtmp: 'RTMP [h]',
      chartLabelHls: 'HLS [h]',
      chartLabelH5liveToken: 'H5Live Token [h]',
      chartLabelMoq: 'MoQ [h]',
      unitConversion: (value) => roundTo(value / 3600, 3),
      resUnitPropNames: 'seconds',
   },
};

const protocolNameLengthPx = 85;

function defaultChartObject(usageSwitch, labels = [], data1 = [], data2 = [], data3 = [], data4 = [], data5 = []) {
   return {
      steppedLine: 'true',
      labels,
      datasets: [
         {
            label: USAGE_SETTINGS[usageSwitch].chartLabelMoq,
            backgroundColor: colorToRgba(orange, 10),
            borderColor: orange,
            borderWidth: 1,
            data: data1,
         },
         {
            label: USAGE_SETTINGS[usageSwitch].chartLabelH5Live,
            backgroundColor: colorToRgba(light, 10),
            borderColor: light,
            borderWidth: 1,
            data: data2,
         },
         {
            label: USAGE_SETTINGS[usageSwitch].chartLabelRtmp,
            backgroundColor: colorToRgba(red, 10),
            borderColor: red,
            borderWidth: 1,
            data: data3,
         },
         {
            label: USAGE_SETTINGS[usageSwitch].chartLabelHls,
            backgroundColor: colorToRgba(dark, 10),
            borderColor: dark,
            borderWidth: 1,
            data: data4,
         },
         {
            label: USAGE_SETTINGS[usageSwitch].chartLabelH5liveToken,
            backgroundColor: colorToRgba(very_dark, 10),
            borderColor: very_dark,
            borderWidth: 1,
            data: data5,
         },
      ],
   };
}

function addZoomClickHandler(chartOptions, barRef, setModal) {
   return {
      ...chartOptions,
      onClick: (event, item) => {
         if (item.length === 0) {
            return;
         }
         const activePoint = barRef.current.chartInstance.getElementAtEvent(event)[0];
         const datasetIndex = activePoint._datasetIndex;
         const data = activePoint._chart.data;
         const clickedLabel = data.datasets[datasetIndex].label;
         const index = item[0]._index;

         setModal({
            chosenIndex: index,
            toggleShow: true,
            selectedStackSubBar: clickedLabel.split(' [')[0],
         });
      },
   };
}

function totalToString(total) {
   return addSeparatorAndThreeDecimalPlaces(total).toString();
}

function UsagePlayout({ height }) {
   const usageSwitch = useSelector(usageSwitchSelector);

   const [totals, setTotals] = useState([0, 0, 0, 0]);
   const [grandTotal, setGrandTotal] = useState(0);
   const [chart, setChart] = useState(defaultChartObject(usageSwitch));

   const [noData, setNoData] = useState(false);

   const hash = useSelector(hashSelector);
   const streamNames = useSelector(streamNamesSelector);
   const tags = useSelector(tagsSelector);
   const countries = useSelector(countriesSelector);

   const start = useSelector(startTimespanSelector);
   const end = useSelector(endTimespanSelector);

   const timespanObj = useSelector(timespanSelector);
   const interval = useSelector(intervalTimespanSelector);
   const timespanTitle = useSelector(titleTimespanSelector);

   const cancelToken = useSelector(cancelTokenSelector);
   const dispatch = useDispatch();

   const [modal, setModal] = useState({
      toggleShow: false,
      chosenIndex: 0,
      selectedStackSubBar: WIDGET_SETTINGS[PLAYOUT].categoryLabels[0],
   });

   const [timespansPerIndex, setTimespansPerIndex] = useState([
      {
         start: moment.utc(),
         end: moment.utc(),
      },
   ]);

   const barRef = useRef(null);

   const chartOptions = addZoomClickHandler(barChartOptions, barRef, setModal);

   const header = useMemo(() => getHttpHeader(hash), [hash]);
   const urlFilterQuery = getFilterQueryParams(countries, tags, [], streamNames, HOME_VIEW);
   const url = `/v2/usage/protocol/playout/${usageSwitch}/timeseries?from=${start.format()}&to=${end.format()}&interval=${interval}${urlFilterQuery}`;

   const { successful, isLoading, errorMessage, data } = useDataFetch(url, { header, cancelToken });

   useEffect(() => {
      if (successful) {
         const usage = data.data;
         setNoData(data.statusCode === 2000);
         const { datasetsDatas, labels, totals, rawTotals, timespansPerIndex } = generateStackedBarChartData(
            usage,
            timespanObj,
            stackedPropNames,
            USAGE_SETTINGS[usageSwitch].resUnitPropNames,
            USAGE_SETTINGS[usageSwitch].unitConversion,
         );
         setTimespansPerIndex(timespansPerIndex);
         setChart(defaultChartObject(usageSwitch, labels, ...datasetsDatas));
         setTotals(totals);
         setGrandTotal(USAGE_SETTINGS[usageSwitch].unitConversion(rawTotals.reduce((acc, raw) => acc + raw, 0)));
      }
      if (errorMessage) {
         dispatch(setAlarm('danger', errorMessage));
      }
   }, [data, successful, errorMessage, dispatch, usageSwitch, setTimespansPerIndex, timespanObj]);

   const errorOrNoData = errorMessage || noData;

   const { toggleShow, chosenIndex, selectedStackSubBar } = modal;
   const chosenTimespan = timespansPerIndex[chosenIndex];
   const { unit } = USAGE_SETTINGS[usageSwitch];
   const [moqTotal, h5LiveTotal, rtmpTotal, hlsTotal, h5LiveTokenTotal] = totals;

   return (
      <WidgetContainer>
         <WidgetHeaderContainer>
            <HeightSetContainer $heightInPx={23}>
               <TwoSideFlexContainer>
                  <SimpleFlexRow>
                     <CsvExportFromApiButton
                        url={url}
                        header={header}
                        cancelToken={cancelToken}
                        filename={'Usage_Playout'}
                     />
                  </SimpleFlexRow>
                  <SimpleFlexRow>
                     <SimpleFlexItem>
                        <TrafficHint term={'playout'} hasTimeAxis={true} sizePx={16} />
                     </SimpleFlexItem>
                     <HorizontalSeparator
                        color={DefaultGrey(1, 180)}
                        widthInPx={1}
                        heightInPx={18}
                        marginX_InPx={12}
                        marginTopInPx={1}
                     />
                     <SimpleFlexItem>
                        <ZoomHint />
                     </SimpleFlexItem>
                  </SimpleFlexRow>
               </TwoSideFlexContainer>
            </HeightSetContainer>
         </WidgetHeaderContainer>
         <WidgetBodyContainer>
            <TwoSideRow marginRightPx={15}>
               <div>
                  <CardTitle className='mb-0'>Usage Playout</CardTitle>
                  <div className='small text-muted'>{`${timespanTitle}\u00A0\u00A0\u00A0( UTC time )`}</div>
               </div>
               <RightSide minWidthPx={225}>
                  <TwoSideRow setNoWidth={true} marginLeftPx={15}>
                     <RightSide minWidthPx={protocolNameLengthPx}>MoQ:</RightSide>
                     <ColoredText color={orange}>
                        {`${isLoading || errorOrNoData ? '0' : totalToString(moqTotal)} ${unit}`}
                     </ColoredText>
                  </TwoSideRow>
                  <TwoSideRow setNoWidth={true} marginLeftPx={15}>
                     <RightSide minWidthPx={protocolNameLengthPx}>H5Live:</RightSide>
                     <ColoredText color={light}>
                        {`${isLoading || errorOrNoData ? '0' : totalToString(h5LiveTotal)} ${unit}`}
                     </ColoredText>
                  </TwoSideRow>
                  <TwoSideRow setNoWidth={true} marginLeftPx={15}>
                     <RightSide minWidthPx={protocolNameLengthPx}>RTMP:</RightSide>
                     <ColoredText color={red}>
                        {`${isLoading || errorOrNoData ? '0' : totalToString(rtmpTotal)} ${unit}`}
                     </ColoredText>
                  </TwoSideRow>
                  <TwoSideRow setNoWidth={true} marginLeftPx={15}>
                     <RightSide minWidthPx={protocolNameLengthPx}>HLS:</RightSide>
                     <ColoredText color={dark}>
                        {`${isLoading || errorOrNoData ? '0' : totalToString(hlsTotal)} ${unit}`}
                     </ColoredText>
                  </TwoSideRow>
                  <TwoSideRow setNoWidth={true} marginLeftPx={15}>
                     <RightSide minWidthPx={protocolNameLengthPx}>H5Live Token:</RightSide>
                     <ColoredText color={very_dark}>
                        {`${isLoading || errorOrNoData ? '0' : totalToString(h5LiveTokenTotal)} ${unit}`}
                     </ColoredText>
                  </TwoSideRow>
                  <TwoSideRow setNoWidth={true} marginLeftPx={15}>
                     <RightSide minWidthPx={protocolNameLengthPx}>Total:</RightSide>
                     <div>
                        {`${isLoading || errorOrNoData ? '0' : totalToString(grandTotal)} ${unit}`}
                     </div>
                  </TwoSideRow>
               </RightSide>
            </TwoSideRow>
            <ChartWrapper className='chart-wrapper' marginTopPx={10} height={height}>
               <Spinner loading={isLoading} parentTopMarginPx={65}>
                  {errorOrNoData ? (
                     <CenteredMsgShowing height={height}>
                        {errorMessage ? (
                           <ErrorDisplay error={errorMessage} msgColor={'rgb(80,80,80)'}></ErrorDisplay>
                        ) : (
                           'No data available'
                        )}
                     </CenteredMsgShowing>
                  ) : (
                     <SetChartHeight height={height}>
                        <Bar ref={barRef} data={chart} options={chartOptions} height={height} />
                     </SetChartHeight>
                  )}
               </Spinner>
            </ChartWrapper>
         </WidgetBodyContainer>
         {toggleShow && (
            <UsageModal
               timespan={chosenTimespan}
               metric={PLAYOUT}
               selectedStackSubBar={selectedStackSubBar}
               closeModal={() => setModal({ ...modal, toggleShow: false })}
            />
         )}
      </WidgetContainer>
   );
}

UsagePlayout.propTypes = {
   height: PropTypes.number,
};

export default UsagePlayout;
